import { MediaLevel, Prisma } from '.prisma/client';
import { useCreatePlaylist, useUpdatePlaylist } from '@/hooks/api';
import { Category } from '@/prisma/utils/categories';
import { MediaLevels } from '@/prisma/utils/media-levels';
import { MediaType } from '@/prisma/utils/media-type';
import { Playlist } from '@/prisma/utils/playlist';
import { Tag } from '@/prisma/utils/tag';
import { useDisclosure } from '@chakra-ui/react';
import React, { useEffect, useState } from 'react';
import Modal, { ModalSaveEventHandler } from '../modal';
import { PlaylistForm } from './form';

export function usePlaylistEditor() {
	const {
		isOpen, onOpen: openEditor, onClose: closeEditor, 
	} = useDisclosure();

	return {
		isOpen,
		openEditor,
		closeEditor,
		Editor: PlaylistEditor,
	};
}

type PlaylistEditorProps = {
	isOpen: boolean;
	openEditor: () => void;
	closeEditor: () => void;
	playlist: Playlist;
	mediaTypes: MediaType[];
	mediaLevels: MediaLevels;
	mediaCategories: Category[];
}

export const PlaylistEditor: React.FC<PlaylistEditorProps> = (props: PlaylistEditorProps) => {
	const {
		isOpen, playlist, mediaTypes, mediaLevels, mediaCategories, openEditor, closeEditor 
	} = props;

	const initialRef = React.useRef();
	const finalRef = React.useRef();

	const [name, setName] = useState(playlist?.name);
	const [description, setDescription] = useState(playlist?.description);
	const [type, setType] = useState<MediaType>(playlist?.type as MediaType);
	const [level, setLevel] = useState(playlist?.level);
	const [categories, setCategories] = useState(playlist?.categories || []);
	const [tags, setTags] = useState<(Tag | Prisma.TagCreateInput)[]>(playlist?.tags || []);

	const [mediaUrls, setMediaUrls] = useState('');
	const [errorMessage, setErrorMessage] = useState('');

	const createPlaylist = useCreatePlaylist();
	const updatePlaylist = useUpdatePlaylist();
	
	const handleCreatePlaylist: ModalSaveEventHandler = () => {
		setErrorMessage('');
		createPlaylist.mutate({
			slug: null,
			name,
			description,
			type: type.slug,
			level,
			categories,
			tags,
			items: [mediaUrls],
		}, {
			onSuccess: closeEditor,
			onError: (e) => {
				setErrorMessage(e.error.message);
			},
		});
	};

	const handleUpdatePlaylist: ModalSaveEventHandler = () => {
		setErrorMessage('');
		updatePlaylist.mutate({
			id: playlist.id,
			slug: null,
			name,
			description,
			type: type.slug,
			level,
			categories,
			tags,
			items: [mediaUrls],
		}, {
			onSuccess: closeEditor,
			onError: (e) => setErrorMessage(e.message),
		});
	};

	useEffect(() => {
		setName(playlist?.name);
		setDescription(playlist?.description);
		setType(playlist?.type);
		setLevel(playlist?.level);
	}, [playlist]);

	const form = <PlaylistForm
		name={name}
		onNameChange={(e) => setName(e.target.value)}
		description={description}
		onDescriptionChange={(e) => setDescription(e.target.value)}
		type={type}
		types={mediaTypes}
		onTypeChange={(e) => setType(e)}
		level={level}
		levels={mediaLevels}
		onLevelChange={(e) => setLevel(e.target.value as MediaLevel)}
		categories={mediaCategories}
		selectedCategories={categories}
		onCategoriesChange={(_cs) => setCategories(_cs)}
		tags={tags as Tag[]}
		onTagsChange={(_ts) => setTags(_ts)}
		mediaUrls={mediaUrls}
		onMediaUrlsChange={(e) => setMediaUrls(e.target.value)}
		initialRef={initialRef}
		errorMessage={errorMessage}
	/>;

	return (
		<>
			{!playlist && <Modal
				initialRef={initialRef}
				title="Add playlist"
				isOpen={isOpen}
				onCancel={closeEditor}
				onSubmit={handleCreatePlaylist}
			>
				{form}
			</Modal>}
			{playlist && <Modal
				initialRef={initialRef}
				title={`Edit playlist ${playlist.id}`}
				isOpen={isOpen}
				onSubmit={handleUpdatePlaylist}
				onCancel={closeEditor}
			>
				{form}
			</Modal>}
		</>
	);
};
